// `RefLabel` (design-system spec §3.9 — the honest-reference states).
//
// A value that came from ANOTHER read — a work centre's name, a waste
// category's, a price class's — has four states, and three of them used to
// render identically across this product:
//
//   1. RESOLVED   — the name. Plain text.
//   2. ABSENT     — the row genuinely has no reference (`cause_code_id: null`
//                   on a category whose `requires_cause` is false). An em dash
//                   in the read-only ink: "there is nothing here", said out
//                   loud rather than left as an empty cell.
//   3. RESOLVING  — the index that would name it is still in flight. A marker,
//                   because this state fixes itself in 200 ms.
//   4. UNRESOLVED — the index answered and has no row for this id (it is on a
//                   later page, or archived out of the picker). The SHORT id in
//                   a mono chip, which reads as an identifier rather than as a
//                   broken name.
//
// States 3 and 4 are different facts and used to be the same raw uuid; state 2
// used to be an empty cell, indistinguishable from a value the screen simply
// failed to render. A uuid in a name column reads as corrupt data, and a blank
// one reads as "nothing was measured".
//
// The chip is deliberately 8 characters, taken from the uuid's random TAIL
// (`lib/short-id.ts`): enough to quote in a support thread, short enough that
// nobody mistakes it for a name, and different for two ids minted in the same
// minute, which a v7's time prefix is not. Only a UUID-SHAPED id is
// cut. A natural key (an audit row's `entity_id` is one for two writers) is
// shown whole, because its first eight characters name nothing. The chip
// carries the full value on its title either way, and a `copyable` chip is a
// button that puts the full value on the clipboard, for screens whose own
// filters take the id (the audit trail, the quality queues).
//
// WHY IT IS HERE AND NOT IN AN AREA. Modules 04 and 06 each grew their own copy
// (`areas/office/production/components/ref-label.tsx`,
// `areas/office/inventory/components/inventory-ui.tsx`) before this one existed,
// and module 05 needed a third. This is the register's version — it names only
// register classes (`.ref-pending`, `.ref-id`, `.ref-none` in styles/office.css)
// instead of an area's, which is what made the module-04 copy unusable outside
// module 04: its absent state is painted by `.production-hint`, a class declared
// in `production.css` and loaded only when a production route has been opened.
// Folding those two onto this one is NAMED DEBT for UX-5, not done here — UX-4
// does not move screens it is not migrating (the rule `styles/office.css`
// already states for `.ds-field`), and both areas are certified and at zero.

import { useContext, useState, type MouseEvent } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRefName, type RefKind } from "../lib/ref-names";
import { shortId } from "../lib/short-id";

// THE NAME SOURCE (P3-UX UP6, D-441). A screen that has no index to pass a
// `value` from passes the reference's `kind` instead, and the label asks the
// batched names read (`lib/ref-names.ts`, `GET /v1/refs`) itself: every label
// mounted in one tick costs one request, cached per kind and id. A name the
// reader may not see comes back absent and stays the short-id chip, which is
// the same state as an id the index never resolved (spec 16 §3.13).

export interface RefLabelProps {
  /** The resolved name, or `undefined` when the index has no row for `id`. */
  value?: string | undefined;
  /** The reference itself. `null`/`undefined` means the row HAS no reference. */
  id: string | null | undefined;
  /** True while the index that would resolve `id` is still fetching. */
  pending?: boolean;
  /** Ask the names read for `id` when the caller holds no `value`. */
  kind?: RefKind;
  /** Render the unresolved chip as a button that copies the full id. */
  copyable?: boolean;
}

/** What the chip prints: the last eight characters of a uuid, or a natural
 * key whole (`lib/short-id.ts`). */
export function shortRef(id: string): string {
  return shortId(id);
}

function CopyChip({ id }: { id: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = (event: MouseEvent<HTMLButtonElement>): void => {
    // The chip sits in table rows whose click opens a peek; copying is not that.
    event.stopPropagation();
    // `clipboard` is absent outside a secure context, whatever the DOM types say.
    const clipboard = (navigator as { clipboard?: Clipboard }).clipboard;
    if (clipboard === undefined) return;
    void clipboard.writeText(id).then(
      () => {
        setCopied(true);
      },
      () => {
        setCopied(false);
      },
    );
  };
  return (
    <button
      type="button"
      className="ref-id ref-id-copy"
      title={copied ? t("ref.copied") : id}
      aria-label={t("ref.copy", { id })}
      onClick={copy}
    >
      {shortRef(id)}
    </button>
  );
}

export function RefLabel({ value, id, pending = false, kind, copyable = false }: RefLabelProps) {
  if (kind !== undefined && (value === undefined || value.length === 0)) {
    return <NamedRefLabel kind={kind} id={id} copyable={copyable} />;
  }
  return <RefLabelView value={value} id={id} pending={pending} copyable={copyable} />;
}

function NamedRefLabel({
  kind,
  id,
  copyable,
}: {
  kind: RefKind;
  id: string | null | undefined;
  copyable: boolean;
}) {
  // A tree with no query client (an isolated screen test, a story) cannot ask;
  // it shows the short-id chip, which is what an unanswered ask shows anyway.
  const client = useContext(QueryClientContext);
  if (client === undefined)
    return <RefLabelView value={undefined} id={id} pending={false} copyable={copyable} />;
  return <AskedRefLabel kind={kind} id={id} copyable={copyable} />;
}

function AskedRefLabel({
  kind,
  id,
  copyable,
}: {
  kind: RefKind;
  id: string | null | undefined;
  copyable: boolean;
}) {
  const { name, pending } = useRefName(kind, id);
  return <RefLabelView value={name} id={id} pending={pending} copyable={copyable} />;
}

function RefLabelView({
  value,
  id,
  pending,
  copyable,
}: {
  value: string | undefined;
  id: string | null | undefined;
  pending: boolean;
  copyable: boolean;
}) {
  const { t } = useTranslation();
  if (value !== undefined && value.length > 0) return <>{value}</>;
  if (id === null || id === undefined || id.length === 0) {
    return (
      <span className="ref-none" title={t("ref.none")}>
        —
      </span>
    );
  }
  if (pending) {
    return (
      <span className="ref-pending" aria-label={t("ref.resolving")}>
        …
      </span>
    );
  }
  if (copyable) return <CopyChip id={id} />;
  return (
    <code className="ref-id" title={id}>
      {shortRef(id)}
    </code>
  );
}
